import { Badge, Table, VStack, HStack, Input, Button } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDateTime } from "../Utils/newHelper";
import AllTradesSkeleton from "../Components/custom/Skeletons/AllTradesSkeleton";
import { CustomPagination } from "../Components/custom/CustomPagination";
import { getAllTradesList } from "../Redux/Action/transaction/allTradesAction";
import { PAGINATION } from "../Utils/Constants";
import { setDynamicTitle } from "../Utils/dynamicTitleManager";

export const AllTrades = () => {
  // Use dynamic title management
  useEffect(() => {
    setDynamicTitle("All Trades");
  }, []);

  const didMountRef = useRef(false);
  const [allTrades, setAllTrades] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const today = tomorrow.toISOString().split("T")[0];
  // Set start date to 29 days before end date to ensure exactly 29 days difference
  const startDate = new Date(tomorrow.getTime() - 29 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(formatDate(startDate));
  const [to, setTo] = useState(formatDate(tomorrow));
  const [localFrom, setLocalFrom] = useState(formatDate(startDate));
  const [localTo, setLocalTo] = useState(formatDate(tomorrow));
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("recent"); // Default filter
  const [searchTerm, setSearchTerm] = useState(""); // Search term state
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(""); // Debounced search term
  
  const dispatch = useDispatch();
  const allTradesData = useSelector((state) => state.AllTradesList?.allTradesData);

  // State for animated placeholder
  const [placeholderText, setPlaceholderText] = useState("Account No");
  const [isAnimating, setIsAnimating] = useState(false);

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Handle animated placeholder effect
  useEffect(() => {
    const texts = ["Account No", "Deal No"];
    let currentIndex = 0;
    
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        currentIndex = (currentIndex + 1) % texts.length;
        setPlaceholderText(texts[currentIndex]);
        setIsAnimating(false);
      }, 500); // Half of the 1 second for animation
    }, 2000); // Change text every 2 seconds (1s for animation + 1s static)

    return () => clearInterval(interval);
  }, []);

  // Debounce effect for search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to first page when search term actually changes (after debounce)
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1);
      }
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const handleDateApply = () => {
    setFrom(localFrom);
    setTo(localTo);
    setCurrentPage(1); // Reset to first page when date range changes
  };

  const fetchData = () => {
    const payload = {
      accountno: "all",
      sdate: `${from} 00:00:00`,
      edate: `${to} 23:59:59`,
      per_page: PAGINATION.PER_PAGE,
      page: currentPage,
      filter: filter, // Add filter to payload
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }) // Add search term to payload if it exists
    };
    
    setIsDataLoading(true);
    dispatch(
      getAllTradesList({
        payload,
        callback: (data) => {
          if (data?.meta?.code === 200) {
            // Updated to match the actual API response structure
            setAllTrades(data?.data?.data || []);
            setTotalPages(data?.data?.pagination?.totalPages || 0);
            setIsDataLoading(false);
          } else {
            setIsDataLoading(false);
          }
        },
      })
    );
  };

  useEffect(() => {
    if (didMountRef.current) {
      const timer = setTimeout(fetchData, 500);
      return () => clearTimeout(timer);
    } else {
      fetchData();
      didMountRef.current = true;
    }
  }, [currentPage, from, to, filter, debouncedSearchTerm]); // Add debouncedSearchTerm to dependency array

  return (
    <VStack>
      <HStack width="100%" justifyContent="space-between" alignItems="flex-end">
        <HStack spacing={4} alignItems="flex-end">
          <HStack spacing={3} alignItems="flex-end">
            <div>
              <label htmlFor="from-date" style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem' }}>From</label>
              <Input
                id="from-date"
                type="date"
                value={localFrom}
                max={today}
                onChange={(e) => setLocalFrom(e.target.value)}
                size="sm"
                width="150px"
              />
            </div>

            <div>
              <label htmlFor="to-date" style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem' }}>To</label>
              <Input
                id="to-date"
                type="date"
                value={localTo}
                min={localFrom}
                max={today}
                onChange={(e) => setLocalTo(e.target.value)}
                size="sm"
                width="150px"
              />
            </div>

            <Button onClick={handleDateApply} colorScheme="blue" size="sm">
              Apply
            </Button>
          </HStack>
          <div style={{ position: 'relative', width: '250px' }}>
            <Input
              placeholder=""
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
               
              }}
              width="100%"
              size="sm"
            />
            <div 
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#a0aec0',
                fontSize: '0.875rem',
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span>Search By&nbsp;</span>
              <span 
                style={{
                  display: 'inline-block',
                  transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                  transform: isAnimating ? 'translateY(-10px)' : 'translateY(0)',
                  opacity: isAnimating ? 0 : 1
                }}
              >
                '{placeholderText}'
              </span>
            </div>
          </div>
        </HStack>
        <select 
          value={filter} 
          onChange={(e) => {
            setFilter(e.target.value);
            setCurrentPage(1); // Reset to first page when filter changes
          }}
          style={{
            padding: '0.5rem',
            borderRadius: '0.375rem',
            border: '1px solid #e2e8f0',
            backgroundColor: 'white',
            color: 'inherit',
            fontSize: '0.875rem',
            width: '250px' // Match width with other inputs
          }}
        >
          <option value="recent">Recent Orders</option>
          <option value="lowToHigh">Quantity: Low to High</option>
          <option value="highToLow">Quantity: High to Low</option>
          <option value="maxProfit">Maximum Profit</option>
          <option value="minProfit">Minimum Profit</option>
        </select>
      </HStack>

      {isDataLoading ? (
        <AllTradesSkeleton />
      ) : (
        <>
          <Table.ScrollArea borderWidth="1px" rounded="md" maxHeight="70vh" width="100%">
            <Table.Root interactive variant="outline" stickyHeader>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader width="10%">Sr.No.</Table.ColumnHeader>
                  <Table.ColumnHeader width="10%" textAlign="center">
                    Account No
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="15%" textAlign="center">
                    Deal No
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="10%" textAlign="center">
                    Symbol
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="8%" textAlign="center">
                    B/S
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="8%" textAlign="center">
                    Qty
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="10%" textAlign="center">
                    Price
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="10%" textAlign="center">
                    Profit
                  </Table.ColumnHeader>
                  <Table.ColumnHeader width="14%" textAlign="center">
                    Time
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {allTrades?.length ? (
                  allTrades?.map((item, index) => (
                    <Table.Row key={item.DealNo || index}>
                      <Table.Cell>{index + 1}</Table.Cell>
                      <Table.Cell textAlign="center">{item?.Accountno || "N/A"}</Table.Cell>
                      <Table.Cell textAlign="center">{item?.DealNo || "N/A"}</Table.Cell>
                      <Table.Cell textAlign="center">{item?.Symbol || "N/A"}</Table.Cell>
                      <Table.Cell textAlign="center">
                        <Badge variant="surface" colorPalette={item?.BS === "Buy" ? "green" : "red"}>
                          {item?.BS || "N/A"}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell textAlign="center">{item?.Qty || "N/A"}</Table.Cell>
                      <Table.Cell textAlign="center">{item?.Price || "N/A"}</Table.Cell>
                      <Table.Cell textAlign="center">
                        <Badge
                          variant="surface"
                          colorPalette={
                            item?.Profit > 0 
                              ? "green"
                              : item?.Profit < 0
                                ? "red"
                                : "gray"
                          }
                        >
                          {item?.Profit !== undefined ? item.Profit : "N/A"}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell textAlign="center" fontSize="xs">
                        {item?.Time ? formatDateTime(item.Time) : "N/A"}
                      </Table.Cell>
                    </Table.Row>
                  ))
                ) : (
                  <Table.Row>
                    <Table.Cell colSpan={9} textAlign="center">
                      No trade orders found for the selected criteria.
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
          <CustomPagination totalCount={totalPages} current={currentPage} setCurrent={setCurrentPage} />
        </>
      )}
    </VStack>
  );
};